"use client";

import { useState } from "react";
import { KeyRound, Loader2, Pencil, Plus, ShieldAlert } from "lucide-react";
import { BaseModal } from "@/components/ui/modal";
import type { AgenticOverview, CreatedKey, DashboardKey } from "../lib/dashboardTypes";
import { formatAgo, formatDate, formatInt } from "../lib/format";
import { MCP_PATH } from "../lib/paths";
import { API_ACCENT, BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, CARD, INPUT } from "./agenticStyle";
import { CodeBlock, CopyButton } from "./CopyButton";
import { useOrigin } from "./useOrigin";
import type { Result } from "./useAgenticOverview";

type Actions = {
  createKey: (name: string) => Promise<Result<CreatedKey>>;
  renameKey: (id: string, name: string) => Promise<Result<{ row: DashboardKey }>>;
  revokeKey: (id: string) => Promise<Result<{ row: DashboardKey }>>;
};

function NewKeyModal({ created, onClose }: { created: CreatedKey | null; onClose: () => void }) {
  const origin = useOrigin();
  // Which key was copied, not a flag: the modal stays mounted between keys,
  // so a flag would carry "copied" over to the next key.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const key = created?.key ?? "";
  const copied = key !== "" && copiedKey === key;
  return (
    <BaseModal
      isOpen={!!created}
      onClose={onClose}
      maxWidth="max-w-[620px]"
      labelledBy="new-key-title"
      closeOnBackdrop={false}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(45,212,191,0.12)" }}>
          <KeyRound className="h-5 w-5" style={{ color: API_ACCENT }} aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 id="new-key-title" className="text-lg font-semibold text-white">
            {created?.row.name ?? "Your new key"}
          </h2>
          <p className="mt-1 text-sm text-[#c7d5e0]">
            Copy it now. We keep only a fingerprint of it, so this is the one time it&apos;s shown.
          </p>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2 rounded-lg border border-[#2dd4bf]/40 bg-[#0b121a] p-2 pl-3">
        <code className="min-w-0 flex-1 select-all break-all font-mono text-[13px] text-white" data-ph-mask>
          {key}
        </code>
        <CopyButton text={key} label="Copy key" onCopied={() => setCopiedKey(key)} />
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-[#8f98a0]">
        <ShieldAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden />
        Treat it like a password: keep it in an environment variable or a secret manager, never in a page&apos;s code
        or a public repository. Lost it? Revoke it and create another.
      </p>
      <div className="mt-5 space-y-3">
        <CodeBlock
          label="Try it (free call)"
          code={`curl -H "Authorization: Bearer ${key}" \\\n  ${origin}/api/v1/account/usage`}
        />
        <CodeBlock
          label="Add to Claude Code"
          code={`claude mcp add --transport http steampeek ${origin}${MCP_PATH} \\\n  --header "Authorization: Bearer ${key}"`}
        />
      </div>
      <div className="mt-6 flex justify-end">
        <button type="button" onClick={onClose} className={copied ? BTN_PRIMARY : BTN_SECONDARY}>
          {copied ? "Done" : "I've saved it"}
        </button>
      </div>
    </BaseModal>
  );
}

function KeyRow({ k, nowMs, actions }: { k: DashboardKey; nowMs: number; actions: Actions }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(k.name);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<"rename" | "revoke" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    const next = name.trim();
    if (!next || next === k.name) {
      setEditing(false);
      setName(k.name);
      return;
    }
    setBusy("rename");
    const r = await actions.renameKey(k.id, next);
    setBusy(null);
    if (r.ok) {
      setEditing(false);
      setError(null);
    } else setError(r.message);
  }

  async function revoke() {
    setBusy("revoke");
    const r = await actions.revokeKey(k.id);
    setBusy(null);
    if (!r.ok) setError(r.message);
    setConfirming(false);
  }

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <form onSubmit={saveName} className="flex max-w-sm items-center gap-2">
              <input
                className={INPUT}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                autoFocus
                aria-label="Key name"
              />
              <button type="submit" className={BTN_SECONDARY} disabled={busy === "rename"}>
                {busy === "rename" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Save"}
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-white">{k.name}</span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded p-1 text-[#5c6b7a] transition-colors hover:text-white"
                aria-label={`Rename ${k.name}`}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          )}
          <p className="mt-1 font-mono text-[12px] text-[#8f98a0]">{k.hint}</p>
          <p className="mt-1.5 text-xs text-[#8f98a0]">
            Created {formatDate(k.createdAt)} · last used {formatAgo(k.lastUsedAt, nowMs)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <p className="text-right text-xs tabular-nums text-[#8f98a0]">
            <span className="text-sm font-semibold text-white">{formatInt(k.monthUnits)}</span> units ·{" "}
            {formatInt(k.monthRequests)} calls this month
          </p>
          {confirming ? (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setConfirming(false)} className={BTN_SECONDARY} disabled={busy === "revoke"}>
                Keep
              </button>
              <button type="button" onClick={revoke} className={BTN_DANGER} disabled={busy === "revoke"}>
                {busy === "revoke" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Revoke now
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirming(true)} className="text-xs font-medium text-red-300 hover:text-red-200">
              Revoke
            </button>
          )}
        </div>
      </div>
      {confirming && (
        <p className="mt-2 text-xs text-red-200/90">
          Anything using this key stops working at once. This can&apos;t be undone.
        </p>
      )}
      {error && (
        <p className="mt-2 text-xs text-red-300" role="alert">
          {error}
        </p>
      )}
    </li>
  );
}

export function KeysPanel({
  overview,
  nowMs,
  actions,
  paused = false,
}: {
  overview: AgenticOverview;
  nowMs: number;
  actions: Actions;
  /**
   * The owner isn't on Studio any more (it lapsed or was cancelled): their
   * keys are listed to rename or revoke, and no key can be created.
   */
  paused?: boolean;
}) {
  const live = overview.keys.filter((k) => !k.revokedAt);
  const revoked = overview.keys.filter((k) => k.revokedAt);
  const max = overview.limits.maxActiveKeys;
  const atLimit = live.length >= max;
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedKey | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const n = name.trim() || (live.length === 0 ? "My first key" : `Key ${live.length + 1}`);
    setCreating(true);
    const r = await actions.createKey(n);
    setCreating(false);
    if (r.ok) {
      setCreated(r.value);
      setName("");
      setError(null);
    } else setError(r.message);
  }

  return (
    <section className={CARD} aria-labelledby="keys-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="keys-heading" className="text-lg font-semibold text-white">
          API keys
        </h2>
        <p className="text-xs tabular-nums text-[#8f98a0]">
          {paused ? `${live.length} paused` : `${live.length} of ${max} active`}
        </p>
      </div>
      {paused ? (
        <p className="mt-1 text-sm text-[#c7d5e0]">
          You&apos;re not on Studio right now, so these keys are paused: every call with them is refused with 403
          plan_required. They work again as soon as you&apos;re back on Studio. Revoke any you no longer need.
        </p>
      ) : (
        <p className="mt-1 text-sm text-[#8f98a0]">
          One key per app or agent makes it easy to see who spends what and to cut one off without touching the others.
        </p>
      )}

      {!paused && (
        <form onSubmit={create} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input
            className={INPUT}
            placeholder={live.length === 0 ? "Name it, e.g. \"Claude Code\" or \"Weekly report\"" : "Name for the new key"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            disabled={atLimit || creating}
            aria-label="New key name"
          />
          <button type="submit" className={`${BTN_PRIMARY} shrink-0 whitespace-nowrap sm:w-auto`} disabled={atLimit || creating}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
            Create key
          </button>
        </form>
      )}
      {!paused && atLimit && (
        <p className="mt-2 text-xs text-[#8f98a0]">You have {max} active keys, the most a plan can hold. Revoke one to create another.</p>
      )}
      {error && (
        <p className="mt-2 text-xs text-red-300" role="alert">
          {error}
        </p>
      )}

      {live.length > 0 ? (
        <ul className="mt-5 divide-y divide-[#2a475e]/60">
          {live.map((k) => (
            <KeyRow key={k.id} k={k} nowMs={nowMs} actions={actions} />
          ))}
        </ul>
      ) : (
        <p className="mt-5 rounded-lg border border-dashed border-[#2a475e] p-4 text-center text-sm text-[#8f98a0]">
          No keys yet. Create one to call the API or connect an AI agent.
        </p>
      )}

      {revoked.length > 0 && (
        <details className="group mt-5">
          <summary className="cursor-pointer list-none text-xs font-medium text-[#8f98a0] hover:text-white">
            <span className="mr-1.5 inline-block transition-transform group-open:rotate-90">›</span>
            {revoked.length} revoked key{revoked.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-3 space-y-2">
            {revoked.map((k) => (
              <li key={k.id} className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-[#5c6b7a]">
                <span>
                  <span className="text-[#8f98a0] line-through">{k.name}</span>
                  <span className="ml-2 font-mono">{k.hint}</span>
                </span>
                <span>
                  revoked {formatDate(k.revokedAt)}
                  {k.monthUnits > 0 && ` · ${formatInt(k.monthUnits)} units this month`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <NewKeyModal created={created} onClose={() => setCreated(null)} />
    </section>
  );
}
